// apps/web/src/pages/account/EmailChange.tsx
//
// Plan 04-12b Task 3 — initiate email change.
//
// FLOW (CONTEXT D-14 + Plan 04-10):
//   Step 1 (this page): user enters new_email → POST
//     /api/auth/account/email-change/request → server sends:
//       - Confirmation link to NEW address
//       - Notification email to OLD address
//   Step 2 (the email link): user clicks the confirmation link in the new
//     inbox → server presents a /confirm-email-change page (out of scope
//     for v1; Plan 12b/Phase 8 ships if needed). For now the user simply
//     clicks the link → server's GET handler completes the change after
//     re-auth + MFA (Plan 04-10's confirm flow).

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { EmailField } from "@/components/forms/EmailPasswordFields";

const emailChangeSchema = z.object({
  newEmail: z.string().min(1, "Email is required").email("Invalid email"),
});

type EmailChangeValues = z.infer<typeof emailChangeSchema>;

export function EmailChange(): React.ReactElement {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<EmailChangeValues>({
    resolver: zodResolver(emailChangeSchema),
    defaultValues: { newEmail: "" },
    mode: "onSubmit",
  });

  async function onSubmit(values: EmailChangeValues): Promise<void> {
    setServerError(null);
    try {
      await api<{ ok: true }>("/api/auth/account/email-change/request", {
        method: "POST",
        body: JSON.stringify({ new_email: values.newEmail }),
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { error?: { code?: string; message?: string } };
        const body = err.body as ErrBody | null;
        const code = body?.error?.code;
        if (code === "TARGET_EMAIL_CLAIMED") {
          setServerError(
            "That email is already in use by another account.",
          );
          return;
        }
        if (code === "PENDING_ALREADY") {
          setServerError(
            "You already have a pending email change. Wait for it to expire or cancel from your email.",
          );
          return;
        }
        if (code === "NO_OP_SAME_EMAIL") {
          setServerError("That's already your email.");
          return;
        }
        setServerError(body?.error?.message ?? "Could not start the email change");
        return;
      }
      setServerError(err instanceof Error ? err.message : "Could not start the email change");
    }
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Change email address</CardTitle>
          <CardDescription>
            We'll send a confirmation link to your new address and notify your
            current address. You'll re-enter your password and MFA code to
            finalize the change.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <Alert>
              <AlertTitle>Check your new email address</AlertTitle>
              <AlertDescription>
                We sent a confirmation link to your new address and a
                notification to your current address. The link expires in 15
                minutes.
              </AlertDescription>
            </Alert>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                {serverError !== null ? (
                  <Alert variant="destructive">
                    <AlertTitle>Could not start</AlertTitle>
                    <AlertDescription>{serverError}</AlertDescription>
                  </Alert>
                ) : null}
                <EmailField
                  control={form.control}
                  name="newEmail"
                  label="New email address"
                  placeholder="newemail@example.com"
                  autoComplete="email"
                  autoFocus
                  disabled={form.formState.isSubmitting}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? "Sending…" : "Send confirmation link"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            <Link to="/account/security" className="font-medium underline">
              Back to security
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
